<script>
import debounce from 'lodash/debounce';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BlockingContainer from '@components/BlockingContainer';
import Layout from '@layouts/main';
import AdminModel from '@models/Admin';
import HeaderComponent from '@components/HeaderComponent';

export default {
  page() {
    return {
      title: this.$t('titles.admins'),
    };
  },
  components: {
    Layout,
    FontAwesomeIcon,
    BlockingContainer,
    HeaderComponent,
  },
  data() {
    return {
      sortBy: 'last_name',
      sortDesc: false,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      fieldsTable: [
        { key: 'id', sortable: true },
        { key: 'last_name', label: this.$t('table_labels.last_name'), sortable: true },
        { key: 'first_name', label: this.$t('table_labels.first_name'), sortable: true },
        { key: 'email', label: this.$t('table_labels.email'), sortable: true },
        { key: 'roles', label: this.$t('table_labels.roles'), sortable: false },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      isBusy: false,
      filters: {
        search: null,
      },
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
  },
  watch: {
    filters: {
      handler() {
        this.$refs.asyncTable.refresh();
      },
      immediate: false,
      deep: true,
    },
  },
  methods: {
    itemsProvider(ctx) {
      return AdminModel.orderBy(this.sortDir + ctx.sortBy)
        .where('search', this.filters.search)
        .include('roles')
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.meta.total;
          return items || [];
        })
        .catch(() => []);
    },
    async deleteEntity(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.admin') }))) {
        this.$wait.start('driver processing');
        const driver = new AdminModel({ id });
        await driver.delete();
        this.$wait.end('driver processing');
        this.$refs.asyncTable.refresh();
      }
    },
    restore(id) {
      if (confirm(this.$t('alerts.restore_entity', { entity: this.$t('entities.admin') }))) {
        this.$wait.start('admin processing');
        new AdminModel({
          id: `${id}/restore`,
        })
          .save()
          .then(() => {
            this.$refs.asyncTable.refresh();
            this.$wait.end('admin processing');
          })
          .catch(() => {
            this.$wait.end('admin processing');
          });
      }
    },
    updateFilter: debounce(function (value) {
      this.filters.search = value;
    }, 500),
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent
          :title="$t('titles.admins')"
          :button-text="$t('titles.create_admin')"
          @button-pressed="$router.push({ name: 'admins.create' })"
        ></HeaderComponent>
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col sm="12" md="12" lg="4">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.search') }}</span
            >&nbsp;
            <small class="pt-01">
              {{
                $t('search.message', {
                  entity: [$t('search.entities.name'), $t('search.entities.email')].join(', '),
                })
              }}
            </small>
          </template>
        </HeaderComponent>
        <b-input-group>
          <b-form-input :value="filters.search" class="form-control-sm" :placeholder="$t('placeholders.search')" @input="updateFilter"></b-form-input>
          <b-input-group-append>
            <b-btn variant="secondary" size="sm" class="text-white border-gray" :disabled="!filters.search" @click="filters.search = ''">{{
              $t('actions.clear')
            }}</b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
    </b-row>
    <div id="reports-index" class="position-relative">
      <BlockingContainer v-if="$wait.is('driver processing') || isBusy"></BlockingContainer>
      <b-table
        ref="asyncTable"
        small
        striped
        :items="itemsProvider"
        stacked="md"
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fieldsTable"
        :no-sort-reset="true"
        :show-empty="true"
      >
        <!-- custom sorting icons -->
        <template #head()="{ label, field: { key, sortable } }">
          {{ label }}
          <template v-if="sortable">
            <!-- Custom icons -->
            <template>
              <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
              <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
              <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
            </template>
          </template>
        </template>
        <template v-slot:cell(actions)="row">
          <div v-if="row.item.deleted_at == null">
            <b-btn class="mr-2" size="sm" variant="outline-primary" :to="{ name: 'admins.edit', params: { id: row.item.id } }">
              <FontAwesomeIcon :icon="['fas', 'edit']" />
            </b-btn>
            <b-btn size="sm" variant="outline-danger" @click.stop="deleteEntity(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
            </b-btn>
          </div>
          <div v-else>
            <b-btn class="mr-2" size="sm" variant="outline-success" @click="restore(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'trash-restore']" />
            </b-btn>
          </div>
        </template>
        <template v-slot:cell(roles)="row">
          <b-badge v-for="role in row.item.roles" :key="`role${role.id}`" class="mr-2" variant="secondary">{{ role.name }}</b-badge>
        </template>
        <template v-slot:cell(empty)="row">
          <div class="text-center">{{ $t('messages.no_items') }}</div>
        </template>
      </b-table>
    </div>

    <b-row>
      <b-col sm="12">
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
      </b-col>
    </b-row>
  </Layout>
</template>
